import { z } from 'zod';
import {
  AdjudicationSensitivity,
  BUSINESS_SUBJECT_STATUSES,
  CLUB_SUBJECT_STATUSES,
  PERSONAL_SUBJECT_STATUSES,
  SECURITY_SUBJECT_STATUSES,
  WEALTH_SUBJECT_STATUSES,
  type SubjectStatus,
} from './vocab.js';

/**
 * The org's own settings, as the office reads and writes them (`GET`/`PATCH
 * /orgs/:orgId/settings`).
 *
 * `org.settings` is an open key-value bag server-side — `queued_gate_budget` and
 * `auto_bind_default` live in it too, and `effectiveRunSettings` reads keys out
 * of it by name. What this contract carries is what the office actually sets:
 * the sensitivity default and the org's own description of itself — both
 * **core**, so every org has them — plus whatever keys its vertical owns (the
 * club's prose). The rest is the server's, and the PATCH is a read-modify-write
 * that never touches it.
 */

/**
 * The cap on the club's prose. It protects the `club_fit` prompt budget rather
 * than the column — `org.settings` is jsonb and would take a novel — and it
 * lives here because the string now has **two** write surfaces (the admin
 * plane's `POST /admin/orgs` and the office's own settings page) and one cap.
 */
export const CLUB_VALUES_MAX_LENGTH = 2000;

export const CLUB_VALUES_TOO_LONG = `the club's values are capped at ${CLUB_VALUES_MAX_LENGTH} characters — this is a short statement the club-fit reading is held against, not a policy document`;

/**
 * The cap on the org's own description of itself — the same number as the club's
 * values, and deliberately **its own constant** rather than an alias.
 *
 * The two strings are the same size of thing (a paragraph or two, appended to a
 * model's user turn) but they are not the same setting: values are a *lens* one
 * vertical holds people against, and a profile is *who the organisation is*,
 * which every vertical has. One of them moving is not a reason for the other to,
 * and an alias would have made that impossible to express.
 */
export const ORG_PROFILE_MAX_LENGTH = 2000;

export const ORG_PROFILE_TOO_LONG = `the organization's description is capped at ${ORG_PROFILE_MAX_LENGTH} characters — this is a short statement of who you are, not a brochure`;

/**
 * The floor under the sensitivity knob: what a run uses when neither its own
 * config nor the org's settings carry a value (`effectiveRunSettings`), and
 * therefore what the settings page shows as the active option for an org that
 * has never chosen one. Here rather than server-side for exactly that reason —
 * a page that named its own default could show one the server would not use.
 */
export const DEFAULT_ADJUDICATION_SENSITIVITY: AdjudicationSensitivity = 'low';

/* ————————————————————————— research spend ————————————————————————— */

/**
 * **How much research a run does, and what it may spend doing it** — the
 * `research` bag, core rather than any vertical's, and the operator's alone
 * (2026-09-04, Soroush): it is written through `PATCH
 * /admin/orgs/:orgId/settings`, read off `GET /admin/orgs`, and the office's own
 * door refuses the key by name ({@link RESEARCH_SETTINGS_ADMIN_ONLY}).
 *
 * Two keys: the depth each status gets and the cap on one run. The extractor's
 * tier and effort are platform constants, not org settings
 * ([run defaults sweep](../../docs/notes/2026-09-26-run-defaults-sweep.md) Q4):
 * every run still carries both in its config, always at
 * {@link DEFAULT_EXTRACTOR_TIER} and {@link DEFAULT_EXTRACTOR_EFFORT}.
 */

/**
 * The model tier the **extractor** runs at. Not the summary legs: those stay
 * Opus by design, because they are the prose the office reads.
 */
export const EXTRACTOR_TIERS = ['opus', 'sonnet'] as const;
export const ExtractorTier = z.enum(EXTRACTOR_TIERS);
export type ExtractorTier = z.infer<typeof ExtractorTier>;

/** The extractor's reasoning effort — the same three words the model API uses. */
export const EXTRACTOR_EFFORTS = ['low', 'medium', 'high'] as const;
export const ExtractorEffort = z.enum(EXTRACTOR_EFFORTS);
export type ExtractorEffort = z.infer<typeof ExtractorEffort>;

/**
 * **How much of the research loop a subject is worth**, in three levels.
 *
 * The word names the *depth of the pass*, never the person: `light` is a shorter
 * loop over the three facets that establish who someone is, not a judgment that
 * the person matters less. What each level actually resolves to — which facets,
 * how many rounds, how many pages — is **core's** table
 * (`server/src/modules/runs/research-depth.ts`), because it is a fact about the
 * pipeline. Which level a *status* gets is the **vertical's** default, below.
 */
export const RESEARCH_DEPTHS = ['full', 'standard', 'light'] as const;
export const ResearchDepth = z.enum(RESEARCH_DEPTHS);
export type ResearchDepth = z.infer<typeof ResearchDepth>;

/**
 * **How much of the loop an *organisation* is worth**, in two levels
 * (organisations-as-subjects note §1 R3a, ruled by Soroush the same night).
 *
 * A separate vocabulary from {@link RESEARCH_DEPTHS} and not three more members
 * of it, because the two tables answer different questions with different
 * words. A person's depth is picked *for* them by their status (a prospect is
 * being decided on, a member is already known); an organisation has no status,
 * so the office picks the depth by pressing one of two buttons — "Look this up"
 * or "Investigate" — and the word on the button is the word here. Folding them
 * into one enum would let `depth_by_status` name `lookup` for a club prospect
 * and `full` for an organisation, neither of which resolves to anything.
 *
 * What each level costs is **core's** table
 * (`server/src/modules/runs/research-depth.ts`, `ORG_DEPTH_PLANS`), exactly as
 * it is for a person: the facets, the search and fetch caps and the people cap
 * are facts about the pipeline, and a second vertical must not restate them.
 *
 * `lookup` is the default when a client names no depth — the cheaper of the two
 * is what an unqualified "look this up" should cost.
 */
export const ORG_RESEARCH_DEPTHS = ['lookup', 'investigation'] as const;
export const OrgResearchDepth = z.enum(ORG_RESEARCH_DEPTHS);
export type OrgResearchDepth = z.infer<typeof OrgResearchDepth>;

/** The depth an `org_lookup` run takes when the office named none. */
export const DEFAULT_ORG_RESEARCH_DEPTH: OrgResearchDepth = 'lookup';

/** The tier every run's extractor uses. See {@link EXTRACTOR_TIERS}. */
export const DEFAULT_EXTRACTOR_TIER: ExtractorTier = 'opus';

/** The effort every run's extractor uses, except event research's pinned `low`. */
export const DEFAULT_EXTRACTOR_EFFORT: ExtractorEffort = 'medium';

/**
 * **The smallest budget there is: one micro-dollar**, the unit
 * `ai_usage_event.cost_micro_usd` meters in.
 *
 * The floor is not fastidiousness about a millionth of a dollar — it is the
 * boundary where "a cap the office set" stops being expressible. Run creation
 * converts dollars to micro-USD with `Math.round`, so anything under half a
 * micro-dollar lands on `budget_micro_usd: 0` — a cap of *nothing*, which the
 * run config's own schema (`z.number().int().positive()`) does not even permit,
 * and which the pipeline would read as a run that may not start. Refusing it at
 * the door is the only place the two can be made to agree.
 */
export const RESEARCH_BUDGET_MIN_USD = 0.000001;

/**
 * The refusal message for a budget that is neither absent nor an amount.
 *
 * It is attached to **every** bound rather than to one — the floor, and the
 * number itself — because zod's own wording is what a reader would otherwise get
 * for the odd cases: a JSON `1e400` parses to `Infinity`, which no minimum
 * catches. Under zod 3 that took an explicit `.finite()` carrying this message;
 * zod 4 refuses a non-finite number in `z.number()` itself, before any check
 * runs, so the message rides the schema's own `error` instead — otherwise the
 * operator reads "Invalid input: expected number, received Infinity". A budget
 * of zero is not a budget, it is a run that cannot start; a negative one is
 * nothing at all; "none" is spelled by leaving the key out.
 */
export const RESEARCH_BUDGET_INVALID =
  `a research budget is an amount in dollars of at least ${RESEARCH_BUDGET_MIN_USD} ` +
  '(one micro-dollar, the unit spend is metered in) — leave it out for no cap';

/**
 * **A budget, as an amount** — the bounds without the two absences, so the one
 * door that writes it can spell "unset" over one rule.
 *
 * The operator's `PATCH /admin/orgs/:orgId/settings` takes `null` as the clear,
 * because the control is a text field that is always on screen and always has
 * something to say. The office has no spelling for it at all — it has no
 * research door (see {@link RESEARCH_SETTINGS_ADMIN_ONLY}).
 */
export const ResearchBudgetAmount = z
  .number({ error: RESEARCH_BUDGET_INVALID })
  .min(RESEARCH_BUDGET_MIN_USD, RESEARCH_BUDGET_INVALID);

/**
 * **The research settings are the operator's** (decided 2026-09-04, Soroush —
 * the spend limit alone had moved on 2026-09-03).
 *
 * The bag names a depth and a ceiling: how expensive the pipeline is and how
 * much of it runs. None of that is a question the office is
 * placed to answer — it knows who it wants read, not what a round of extraction
 * is worth — and the spend limit had already moved for the sharpest version of
 * the same reason (the engine checks the cap **between rounds** and never aborts
 * a leg already running, so a run overshoots by one wave, which is a thing an
 * operator may set knowing it and not a ceiling to show an office that would
 * read it as absolute).
 *
 * So the office's Settings page carries no research control at all — not even
 * read-only, since a number with no way to change it is a question rather than
 * an answer — its read answers without the key, and this is the sentence its
 * door gives if the key arrives anyway.
 *
 * The message names `research` rather than a key inside it, because the whole
 * bag is what moved: there is no half of it the office may still send, so there
 * is nothing more specific to point at.
 */
export const RESEARCH_SETTINGS_ADMIN_ONLY =
  "'research' is not a setting the office writes — how much research each person gets and the " +
  'spend limit per run are set by the operator, on the admin surface';

/**
 * **The org's research settings**, as they sit in the bag and travel on the
 * operator's read (`GET /admin/orgs`) and write (`PATCH
 * /admin/orgs/:orgId/settings`).
 *
 * **One shape again since 2026-09-04.** There were three for a day — this one,
 * plus an office view and an office write that omitted and then re-declared
 * `budget_usd` — because two doors wrote one bag and each had to be stopped from
 * clearing the other's key. The office has no research door now, so the pair is
 * gone and what is stored, what is answered and what is written are the same
 * object.
 *
 * Every key optional: absent is "the default", and the default is resolved at
 * run creation (the vertical's for depth, no cap for budget). Nothing here is ever resolved server-side into the
 * settings bag itself, for the reason the sensitivity default gives — an org
 * that never chose anything must stay distinguishable from one that chose what
 * the default happens to be today.
 *
 * `depth_by_status` is a record rather than a fixed shape because its keys are
 * the **org's own vertical's** vocabulary, which this package deliberately keeps
 * as a loose union (§0.7). The keys are checked against that org's actual
 * vocabulary server-side, where the vertical is known — a `prospect` key on a
 * business org is a 400 naming `research.depth_by_status.prospect`.
 */
export const ResearchSettings = z
  .object({
    depth_by_status: z.record(z.string(), ResearchDepth).optional(),
    /**
     * A cap on one research run, in dollars. `null` is the same as absent — the
     * admin control sends it when the field is emptied, because "no cap" is a
     * thing the operator chooses back into rather than a key it can delete from
     * a form.
     *
     * **Written and read through the admin plane only** since 2026-09-03, which
     * is now true of every key beside it as well.
     */
    budget_usd: ResearchBudgetAmount.nullable().optional(),
  })
  .strict();
export type ResearchSettings = z.infer<typeof ResearchSettings>;

/* ————————————————————————— import ————————————————————————— */

/**
 * The tier every import session reads its notes at. A platform constant, not an
 * org setting, and an import session has no spend ceiling of its own either: the
 * organization's monthly limit is what stops its reading
 * ([run defaults sweep](../../docs/notes/2026-09-26-run-defaults-sweep.md) Q3,
 * Q4). There is no `import` settings bag; both settings doors refuse the key as
 * unrecognized.
 */
export const DEFAULT_IMPORT_EXTRACTOR_TIER: ExtractorTier = 'opus';

/* ——————————————————— the month's allowance ——————————————————— */

/**
 * **What the whole organization may spend on AI in a calendar month**
 * (2026-09-09, Soroush).
 *
 * A demo org gets an allowance until it becomes a paying customer, and this is
 * it: one number, over every source the ledger meters — a research run, a note
 * read on a record, an identity match, an imported file — because what it caps
 * is the *account*, not a machine inside it.
 *
 * **A top-level key rather than a key in `research`, and rather than a bag of
 * its own.** The research bag prices one machine; this is about all of them at
 * once, so it does not belong there, and a bag holding it alone would be a
 * shape waiting for a second member no one can name. It sits beside it as its
 * own core key.
 *
 * **The operator's**, for the reason the research bag is: it is what Arbiter is
 * willing to spend on a prospect, which is not a question the office is placed
 * to answer, and the office is never told the figure — see
 * {@link MONTHLY_SPEND_CEILING_ADMIN_ONLY} and, on the wire, the one boolean
 * `GET /needs-action` carries in its place.
 */

/**
 * The floor: **a cent**, not the micro-dollar the per-run budget uses.
 *
 * The difference is what the number is for. That is a per-run cap an operator
 * may deliberately set to nearly nothing while testing, and the floor there is only where "a cap" stops being expressible. A
 * month's allowance for an organization is a figure someone writes on an
 * invoice, and there is no month worth allowing a millionth of a dollar for —
 * so the floor is the smallest amount the currency has, and the precision below
 * matches it.
 */
export const MONTHLY_SPEND_CEILING_MIN_USD = 0.01;

/**
 * The ceiling on the ceiling: a million dollars.
 *
 * Not a budget anyone will set, and that is the point — it is a typo guard. An
 * allowance is typed into a text field, and a slipped keyboard is the only way a
 * seven-figure number reaches this door.
 */
export const MONTHLY_SPEND_CEILING_MAX_USD = 1_000_000;

/**
 * The refusal, attached to every bound rather than one, as
 * {@link RESEARCH_BUDGET_INVALID} is and for the same reason: a JSON `1e400`
 * parses to `Infinity`, which no maximum catches and which zod 4 refuses inside
 * `z.number()` before any check runs, so the wording has to ride the schema's
 * own `error` or the operator reads "expected number, received Infinity".
 *
 * One sentence for four rules — too small, too large, too precise, not a number
 * at all — because the field it is shown under is one field, and a reader who
 * has just been told what an allowance is does not need to be told which of the
 * four they broke.
 */
export const MONTHLY_SPEND_CEILING_INVALID =
  'a monthly spend limit is an amount in dollars between ' +
  `${MONTHLY_SPEND_CEILING_MIN_USD} and ${MONTHLY_SPEND_CEILING_MAX_USD.toLocaleString('en-US')}, ` +
  'to the cent — send null for no limit';

/**
 * **An allowance, as an amount.** Positive, at most a million, and to the cent.
 *
 * The cents check is a `refine` over the scaled value rather than a
 * `multipleOf`: zod's own would compare floats, and `0.07 % 0.01` is not zero in
 * IEEE 754. Scaling by a hundred and asking how far the result sits from a whole
 * number is the same question asked in a unit that survives being asked.
 */
export const MonthlySpendCeilingAmount = z
  .number({ error: MONTHLY_SPEND_CEILING_INVALID })
  .min(MONTHLY_SPEND_CEILING_MIN_USD, MONTHLY_SPEND_CEILING_INVALID)
  .max(MONTHLY_SPEND_CEILING_MAX_USD, MONTHLY_SPEND_CEILING_INVALID)
  .refine(
    (usd) => Math.abs(usd * 100 - Math.round(usd * 100)) < 1e-6,
    MONTHLY_SPEND_CEILING_INVALID,
  );

/**
 * The refusal the office's door gives a body that names the key — the same
 * arrangement, and the same mechanism, as {@link RESEARCH_SETTINGS_ADMIN_ONLY}
 * one shelf over.
 *
 * It says what the office may do about the limit rather than only that it may
 * not set it, because unlike the research bag this one has a consequence the office
 * *meets*: work stops, and the sentence the banner shows says to contact
 * Arbiter. A door that only said "not yours" would leave a reader who had just
 * been stopped with nowhere to go.
 */
export const MONTHLY_SPEND_CEILING_ADMIN_ONLY =
  "'monthly_spend_ceiling_usd' is not a setting the office writes — the organization's monthly " +
  'spend limit is set by the operator, on the admin surface; contact Arbiter to raise it';

/**
 * **The depth a status gets when the org has chosen none** — the vertical's own
 * default, and the only part of this feature a vertical owns.
 *
 * Here rather than server-side for the reason {@link DEFAULT_ADJUDICATION_SENSITIVITY}
 * gives: the admin page that carries the depth controls shows the effective
 * default beside each unset one, so a page that named its own would show one the
 * server would not use. Each vertical's `Vertical.researchDefaults` reads its own
 * const here, so the page and the run are answering out of one table.
 *
 * **Every status defaults to `full`** (run defaults sweep, 2026-09-26, Soroush).
 * A dossier is priced the same at any depth, so a lighter default sold the
 * customer less for the same credits. Depth stays the operator's per-org
 * override (`research.depth_by_status`), which is how Punta Brava keeps the
 * light pass its members, contacts and declined prospects had before
 * ([the sweep](../../docs/notes/2026-09-26-run-defaults-sweep.md)).
 *
 * The rows stay per vertical because the keys are each vertical's own words and
 * a new vertical states its row, not because they differ today.
 *
 * An unregistered vertical key reads as `undefined` in the index below and the
 * caller renders nothing (§0.3) — it does **not** fall back to club.
 */
export const CLUB_RESEARCH_DEPTH_DEFAULTS: Readonly<
  Record<(typeof CLUB_SUBJECT_STATUSES)[number], ResearchDepth>
> = Object.freeze({
  prospect: 'full',
  member: 'full',
  contact: 'full',
  declined: 'full',
});

export const BUSINESS_RESEARCH_DEPTH_DEFAULTS: Readonly<
  Record<(typeof BUSINESS_SUBJECT_STATUSES)[number], ResearchDepth>
> = Object.freeze({
  intro: 'full',
  friend: 'full',
  partner: 'full',
});

export const PERSONAL_RESEARCH_DEPTH_DEFAULTS: Readonly<
  Record<(typeof PERSONAL_SUBJECT_STATUSES)[number], ResearchDepth>
> = Object.freeze({
  contact: 'full',
});

export const WEALTH_RESEARCH_DEPTH_DEFAULTS: Readonly<
  Record<(typeof WEALTH_SUBJECT_STATUSES)[number], ResearchDepth>
> = Object.freeze({
  prospect: 'full',
  client: 'full',
  contact: 'full',
});

export const SECURITY_RESEARCH_DEPTH_DEFAULTS: Readonly<
  Record<(typeof SECURITY_SUBJECT_STATUSES)[number], ResearchDepth>
> = Object.freeze({
  principal: 'full',
  subject_of_interest: 'full',
  associate: 'full',
});

/**
 * Every vertical's row above, keyed by `org.vertical` exactly as the wire spells it — the
 * same shape as `SUBJECT_VOCABULARY_BY_VERTICAL`, and the client's way to show a
 * default without naming a vertical. Each vertical's server module reads its own
 * const directly, so the map is the *client's* index and never the source.
 */
export const RESEARCH_DEPTH_DEFAULTS_BY_VERTICAL: Readonly<
  Record<string, Readonly<Partial<Record<SubjectStatus, ResearchDepth>>>>
> = Object.freeze({
  club: CLUB_RESEARCH_DEPTH_DEFAULTS,
  business: BUSINESS_RESEARCH_DEPTH_DEFAULTS,
  personal: PERSONAL_RESEARCH_DEPTH_DEFAULTS,
  wealth: WEALTH_RESEARCH_DEPTH_DEFAULTS,
  security: SECURITY_RESEARCH_DEPTH_DEFAULTS,
});

/**
 * `GET /orgs/:orgId/settings`.
 *
 * `null` means **unset**, not "the default": the page shows the default itself,
 * so resolving it server-side would make an org that never chose anything
 * indistinguishable from one that chose Low.
 *
 * **`.strict()`, and it is a tripwire rather than a formality** (2026-09-04).
 * `parseResponse` runs this on every answer the server gives, so a key the
 * server puts on the wire and this shape does not name is a loud 500 instead of
 * a key zod silently strips. That mattered the moment `research` came off:
 * "the office is not told about the operator's bag" is now a claim about a key
 * being **absent**, and absence is exactly what a non-strict object cannot fail
 * on — a `settingsView` that leaked the bag would have answered it to the office
 * and passed every parse.
 *
 * It also makes the union-of-verticals rule enforced rather than assumed: this
 * object is every registered vertical's settings keys plus core's, so a vertical
 * whose `settingsView` answers a key that is not declared here fails at the
 * door. That is the right failure — a key the contract does not carry is one the
 * dashboard could not have rendered anyway.
 */
export const OrgSettingsView = z
  .object({
    /**
     * A **vertical-owned** field, and the reason this object is the *union* of
     * every vertical's keys rather than one fixed shape: the server fills it from
     * `Vertical.settingsView` and never by name, so a club org answers with it and
     * a business org — which owns no settings of its own — answers without it.
     *
     * Hence `.optional()` (widened 2026-09-02 with the business vertical), and
     * hence the meaning of each of the three states, which the page has to tell
     * apart: **absent** is "this org's vertical does not have this setting",
     * **null** is "it has it and nobody has written one", and a string is what the
     * office wrote. Core supplies none of them — a `null` core invented would be
     * indistinguishable from a `null` the office chose.
     *
     * The dashboard already gates the control on `org.vertical` rather than on the
     * key's presence, which is the check that stays right when a vertical owns a
     * key whose value happens to be unset.
     */
    club_values: z.string().nullable().optional(),
    /**
     * **A core key, and therefore required here** — the mirror image of the field
     * above, and the distinction is the whole reason both shapes exist.
     *
     * `club_values` is optional because it is *vertical-owned*: a vertical may own
     * none, so its absence carries the meaning "this org's type does not have this
     * setting". `org_profile` is not any vertical's; every org has one, filled by
     * core beside the sensitivity default, so an org answering without it is a
     * server that has stopped serving a key it owes rather than a vertical
     * declining one. Required, so `parseResponse` says so.
     *
     * `null` is unset, as everywhere else on this object.
     */
    org_profile: z.string().nullable(),
    adjudication_sensitivity_default: AdjudicationSensitivity.nullable(),
    /**
     * **There is deliberately no `research` here** (2026-09-04, Soroush).
     *
     * It was a required object on this view for a day, then the same object minus
     * the spend limit, and now it is gone: the whole bag is the operator's, and
     * operator-only is a rule about the door in both directions — the office
     * neither writes the depth map or the cap, nor reads either. So the key is **absent from the type**, not `null` and not
     * `{}`, and a page reaching for it does not compile.
     *
     * The stored bag is untouched by that and is still core: `GET /admin/orgs`
     * answers it, `settleCoreSettings` settles it, and run creation resolves it
     * exactly as before. What narrowed is this projection.
     *
     * The `.strict()` below is what makes that absence checkable: a leaked bag is
     * an unrecognized key and a 500, not a key quietly dropped on the way out.
     */
  })
  .strict();
export type OrgSettingsView = z.infer<typeof OrgSettingsView>;

/**
 * `PATCH /orgs/:orgId/settings` — an **absent** key is left as it stands, and
 * `club_values: null` (or a string that is blank after trimming) clears it.
 *
 * `.strict()`, unlike the admin plane's passthrough body: this is the office's
 * form, and a key no vertical on this build owns has no business arriving
 * through it.
 *
 * **What this schema is, and what it is not** (settled 2026-09-02 with the
 * business vertical). It is the union of the core knobs and *every* registered
 * vertical's own keys, so it is the first gate and not the deciding one:
 * `.strict()` refuses a key nothing owns, and `VerticalRegistry.settleSettings`
 * refuses a key that belongs to a **different** vertical than the org's — which
 * is how `club_values` on a business org gets its 400, with a message naming the
 * org's actual type rather than zod's "unrecognized key".
 *
 * A per-vertical body was the alternative and was not taken: the office's form
 * posts one object, the org's vertical is already known server-side, and a
 * nested per-vertical bag would put the vertical's name on the wire in a second
 * place that could disagree with `org.vertical`.
 *
 * The business vertical owns no settings, so a business org's PATCH names only
 * core keys and round-trips through here untouched.
 */
export const OrgSettingsUpdate = z
  .object({
    club_values: z
      .string()
      .trim()
      .max(CLUB_VALUES_MAX_LENGTH, CLUB_VALUES_TOO_LONG)
      .nullable()
      .optional(),
    /**
     * The org's own description of itself. **Not nullable, unlike the field
     * above**: blank after trimming is the clear, and it is the only one.
     *
     * `club_values` accepts `null` *and* blank because it had two write doors
     * before it had one rule, and both had to keep meaning "unset". This key has
     * one door and one spelling from the start, so there is nothing to reconcile
     * — the office's form sends the textarea's contents, and an emptied textarea
     * is an empty string. Core deletes the key rather than storing `''`, so
     * "unset" stays one state on the row as well as on the wire.
     */
    org_profile: z.string().trim().max(ORG_PROFILE_MAX_LENGTH, ORG_PROFILE_TOO_LONG).optional(),
    adjudication_sensitivity_default: AdjudicationSensitivity.optional(),
    /**
     * **The research bag, declared here only to be refused** (2026-09-04,
     * Soroush; {@link RESEARCH_SETTINGS_ADMIN_ONLY} has the reasoning).
     *
     * It was a writable key for a day, then a writable key with the spend limit
     * carved out of it, and now none of it is the office's: the bag names the
     * depth and the ceiling, which is the cost and shape
     * of the pipeline rather than anything the office is placed to decide. It is
     * written through `PATCH /admin/orgs/:orgId/settings` and read off `GET
     * /admin/orgs`.
     *
     * `z.undefined()` rather than simply leaving the key off a `.strict()`
     * object, and the difference is worth the line. On the wire the refusal lands
     * on `research` with the sentence above, rather than on the body's root with
     * zod's "unrecognized key" — a client that still posts the bag is told *what
     * happened to it*, not that something it sent was unfamiliar. In the types it
     * makes the key `undefined` rather than absent, so a caller holding a stored
     * {@link ResearchSettings} fails typecheck at the assignment instead of
     * slipping through an excess-property check by way of a variable.
     *
     * With it gone, **every remaining key on this body merges**: absent leaves it
     * alone, present moves it. The replace-whole rule that used to live here went
     * with the bag, and lives on in `settleCoreSettings`, which is where the
     * operator's door settles it.
     */
    research: z.undefined({ error: RESEARCH_SETTINGS_ADMIN_ONLY }).optional(),
    /**
     * **The month's allowance, declared here only to be refused** — the second
     * of the same arrangement, and the one where being told *where the setting
     * went* matters most ({@link MONTHLY_SPEND_CEILING_ADMIN_ONLY}).
     *
     * The office meets this limit: when the month's spend reaches it, new work
     * is refused and Home says so. A door that answered a stale client with
     * zod's "unrecognized key" would leave the one reader who has an actual
     * reason to reach for the setting with nothing to act on.
     */
    monthly_spend_ceiling_usd: z.undefined({ error: MONTHLY_SPEND_CEILING_ADMIN_ONLY }).optional(),
  })
  .strict();
export type OrgSettingsUpdate = z.infer<typeof OrgSettingsUpdate>;
